import { Multiselect, Select, SelectProps } from '@cloudscape-design/components';
import React, { useEffect, useState } from 'react';
// Assuming ItemCheck and OptionDefinition are defined here
import { useGlobalState } from '../../pages/context/GlobalStateContext';
import { ItemCheck, OptionDefinition } from '../../resources/types';

// --- INTERFACES (CHECKS PROP REMOVED) ---

interface OptionGroup {
    label: string;
    options: OptionDefinition[];
}

// When single = true
interface SelectCheckSingleProps {
    single: true;
    visible?: boolean;
    includeDeleted?: boolean;
    includeInactive?: boolean;
    hideTokens?: boolean;
    tokenLimit?: number;
    readonly?: boolean;
    value?: number; // Single value: number (idCheck)
    width?: number;
    checks?: ItemCheck[];
    disabled?: boolean;
    onSelect?: (selectedId: number | undefined) => void;
}

// When single = false
interface SelectCheckMultiProps {
    single?: false; // default is "false"
    visible?: boolean;
    includeDeleted?: boolean;
    includeInactive?: boolean;
    hideTokens?: boolean;
    tokenLimit?: number;
    readonly?: boolean;
    value?: number[]; // Multi value: number[] (idCheck list)
    width?: number;
    checks?: ItemCheck[];
    disabled?: boolean;
    onSelect?: (selectedIds: number[]) => void;
}

export type SelectCheckProps = SelectCheckSingleProps | SelectCheckMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectChecks: React.FC<SelectCheckProps> = (props) => {
    const {
        single,
        value,
        includeDeleted = false,
        includeInactive = false,
        hideTokens = false,
        tokenLimit = 5,
        onSelect,
        readonly,
        width = 200,
        checks = [],
        disabled = false
    } = props;

    // 💡 ACCESSING MASTER SOURCE DIRECTLY
    const { data } = useGlobalState();
    const masterChecks = checks.length > 0 ? checks : data.checks; // Use masterChecks instead of checks prop


    const [checksOptions, setChecksOptions] = useState<SelectProps.Options>([]);

    // --- STATE INITIALIZATION ---

    // Helper function to map ID to OptionDefinition using the master list
    const idToOption = (id: number | undefined | null): OptionDefinition | undefined => {
        if (id === undefined || id === null) return undefined;
        const match = masterChecks.find((c: { idCheck: number; }) => c.idCheck === id);
        return match ? {
            label: match.check,
            value: String(match.idCheck),
            tags: [
                match.isActiveCheck === 0 ? "Inactivo" : null,
                match.isDeleteCheck === 1 ? "Eliminado" : null
            ].filter(Boolean)
        } as OptionDefinition : undefined;
    };


    const [selectedSingle, setSelectedSingle] = useState<OptionDefinition | undefined>(
        single ? idToOption(value as number | undefined | null) : undefined
    );

    const [selectedMulti, setSelectedMulti] = useState<OptionDefinition[]>(
        !single && Array.isArray(value)
            ? value.map(idToOption).filter((x): x is OptionDefinition => x !== undefined)
            : []
    );

    // --- EFFECT TO CALCULATE OPTIONS (Filtering/Grouping/Sorting) ---

    // Recalculates options whenever the global data or filter settings change
    useEffect(() => {
        if (!masterChecks) return;

        // 1. Filter
        const filtered = masterChecks.filter((item: ItemCheck) => {
            if (item.isDeleteCheck === 1) {
                return includeDeleted;
            }
            if (item.isActiveCheck === 0) {
                return includeInactive;
            }
            return true;
        });

        // 2. Group
        const grouped: Record<string, ItemCheck[]> = {};
        filtered.forEach((item: ItemCheck) => {
            const key = item.equipmentCheck || "OTHERS";
            (grouped[key] ||= []).push(item);
        });

        const groups = Object.keys(grouped).map(key => ({
            label: getCategoryLabelInstructor(key),
            options: grouped[key].map(item => ({
                label: item.check,
                value: item.idCheck.toString(),
                tags: [
                    item.isActiveCheck === 0 ? "Inactivo" : null,
                    item.isDeleteCheck === 1 ? "Eliminado" : null
                ].filter(Boolean)
            }))
        }));

        // 3. Sort
        const sortedGroups: SelectProps.Options = [...groups].sort(
            (a, b) => a.label.localeCompare(b.label)
        );

        setChecksOptions(sortedGroups);

        // 4. Update internal state based on new options/filters (if 'value' prop didn't already update it)
        if (single) {
            setSelectedSingle(idToOption(value as number | undefined | null));
        } else if (Array.isArray(value)) {
            setSelectedMulti(value.map(idToOption).filter((x): x is OptionDefinition => x !== undefined));
        }

    }, [masterChecks, includeDeleted, includeInactive, single, value]);


    const placeholder = single ? "Seleccionar chequeo" : selectedMulti && selectedMulti.length > 0 ? `${selectedMulti.length} seleccionado${selectedMulti.length > 1 ? 's' : ''}` : "Seleccionar chequeos";

    return (
        <div style={{ width: `${width}px` }}>
            {/* Single Select Mode */}
            {single && (
                <Select
                    disabled={disabled}
                    readOnly={readonly}
                    placeholder={placeholder}
                    expandToViewport
                    filteringType="auto"
                    options={checksOptions}
                    selectedOption={selectedSingle ?? null}
                    onChange={({ detail }) => {
                        const idString = detail.selectedOption?.value;
                        const fullOption = idToOption(idString ? Number(idString) : undefined);

                        // Update internal state
                        setSelectedSingle(fullOption);

                        // Call external handler with the ID (number) or undefined
                        const selectedId = idString ? Number(idString) : undefined;
                        const singleProps = props as SelectCheckSingleProps;
                        if (singleProps.onSelect) {
                            singleProps.onSelect(selectedId);
                        }
                    }}
                />
            )}

            {/* Multi Select Mode */}
            {!single && (
                <Multiselect
                    disabled={disabled}
                    tokenLimit={tokenLimit}
                    hideTokens={hideTokens}
                    placeholder={placeholder}
                    readOnly={readonly}
                    expandToViewport
                    filteringType="auto"
                    options={checksOptions}
                    selectedOptions={selectedMulti}
                    onChange={({ detail }) => {
                        const list = detail.selectedOptions as OptionDefinition[];
                        setSelectedMulti([...list]);

                        const multiProps = props as SelectCheckMultiProps;

                        if (multiProps.onSelect) {
                            // Map the selected options to an array of numbers
                            const selectedIds = list
                                .map(o => Number(o.value))
                                .filter(id => !isNaN(id));

                            multiProps.onSelect(selectedIds);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default SelectChecks;

// Note: getCategoryLabelInstructor must be defined in scope or imported
function getCategoryLabelInstructor(value: string): string {
    switch (value) {
        case "ALL": return "Todos";
        case "TEO": return "Teórico";
        case "SIM": return "Simulador";
        case "GLIDER": return "Planeador";
        case "PLANE": return "Avión";
        case "HELI": return "Helicóptero";
        case "TOW": return "Remolcador";
        case "COURSE": return "Curso";
        case "ZONE": return "Zona";
        case "OTHERS": return "Otros";
        default: return value;
    }
}